import React from "react";
import {
  AlarmClockOff,
  AlertCircle,
  AlertTriangle,
  CheckCheck,
  Circle,
  CircleCheckBig,
  Clock,
  Loader,
} from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useMeterRunSummary } from "../../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import ActiveCycleTable from "./tabComponents/ActiveCycleTable";
import { Tabs } from "@shared/ui/tabs";
import CompletedCycleTable from "./tabComponents/CompletedCycleTable";

const PhotoMeterReads: React.FC = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const meterType = "PHOTO_METER";
  const { data: summaryData } = useMeterRunSummary({
    remote_utility_id: remoteUtilityId,
    meter_type: meterType,
  });

  const statusCounts = {
    notDispatched:
      summaryData?.result?.notDispatchedRuns ||
      summaryData?.result?.notStartedRuns ||
      0,
    dispatched: summaryData?.result?.dispatchedRuns || 0,
    inProgress: summaryData?.result?.inProgressRuns || 0,
    completed: summaryData?.result?.completedRuns || 0,
    overdue: summaryData?.result?.overdueRuns || 0,
  };

  const tabComponents = {
    active: {
      label: "Active Cycles",
      component: <ActiveCycleTable />,
    },
    history: {
      label: "History",
      component: <CompletedCycleTable />,
    },
  };

  const KpiCardTooltip = ({
    description,
    calculation,
    usage,
  }: {
    description: string;
    calculation: string;
    usage: string;
  }) => (
    <div className="space-y-2">
      <p className="font-medium">{description}</p>
      <p className="text-sm">
        <strong>Calculation:</strong> {calculation}
      </p>
      <p className="text-sm">
        <strong>Usage:</strong> {usage}
      </p>
    </div>
  );
  const kpiCards = [
    {
      title: "Active Cycle",
      value: statusCounts.notDispatched,
      icon: <AlertCircle className="text-purple-700" />,
      short: "Open and eligible for reads",
      description: "Cycles currently open and eligible for meter reading.",
      calculation: "Count of cycles with status = 'active'.",
      usage:
        "Use this to plan daily workload and ensure active cycles have sufficient resources.",
    },
    {
      title: "Dispatched",
      value: statusCounts.dispatched,
      icon: <Circle className="text-slate-700" />,
      short: "Created, not yet assigned",
      description: "Cycles created but not yet assigned to field teams.",
      calculation: "Count of cycles with status = 'not_dispatched'.",
      usage: "Helps supervisors assign work promptly to avoid delays.",
    },
    {
      title: "In Progress",
      value: statusCounts.inProgress,
      icon: <Clock className="text-blue-700" />,
      short: "Teams are executing reads",
      description: "Cycles where teams have started capturing readings.",
      calculation: "Count of cycles with status = 'in_progress'.",
      usage: "Track execution and identify where to unblock teams.",
    },
    {
      title: "Overdue",
      value: statusCounts.overdue,
      icon: <AlertTriangle className="text-amber-700" />,
      short: "Past end date, not completed",
      description: "Cycles past their end date that are not completed.",
      calculation:
        "Count of cycles with endDate < today and status != 'completed'.",
      usage:
        "Prioritize overdue work to reduce revenue leakage and customer impact.",
    },
  ];
  return (
    <div className="w-full mx-auto px-4 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="page-title">Photo Meter Reads</h1>
          <p className="page-subtitle">
            Capture and submit meter readings with a photo for accuracy and
            proof.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card) => (
          <QuickStatsCard
            key={card.title}
            title={card.title}
            count={card.value}
            icon={card.icon}
            tooltip={
              <KpiCardTooltip
                description={card.description}
                calculation={card.calculation}
                usage={card.usage}
              />
            }
          />
        ))}
      </div>

      <Tabs
        defaultValue="active"
        tabComponents={tabComponents}
        tabsListClassName="grid grid-cols-2 gap-2"
        className="space-y-4"
      />
    </div>
  );
};

export default PhotoMeterReads;
